// docs/21 §7: while the storyteller writes a begun World's story. A night sky, a candle and a
// quill, loading lines (writingLines.ts) that turn over every few seconds in a fresh shuffle, and a progress rule that runs to 90 % over
// STORY_ESTIMATE_MS from the request, about how long a story takes, and holds there until the story
// is told (docs/45: no old tale stands in if it runs late), so a slow story never reads as stuck. The
// route moves on when the row flips and the screen has had its floor (core/stores/writing.ts): a story
// already told fills the rule over that floor instead. The party waits together: every player's
// heroes stand on the scene's ground, grouped by player as the lobby seats them (click one for their
// sheet; emotes play over them), beside the World chat, so the wait is spent with the table.
// docs/67: past the estimate the screen says the story is running late; if the module gives up on
// it (the storyteller failed, is stuck, or is not there) the candle goes out, the screen says why,
// and anyone at the table may ask again or go back to the title.
import { useEffect, useState } from 'react';
import { rpc } from '../../../core/net/reducers';
import { useRoute } from '../../../core/stores/route';
import { Button } from '../../components';
import { useSession } from '../../../core/stores/session';
import { tsToMs } from '../../../core/net/connection';
import { deviceNow, serverNow } from '../../../core/time';
import { useWorld, useWorldHeroes, useWorldMembers, useWorldStory } from '../../../core/subscriptions/hooks';
import { identityHex } from '../../../core/net/connection';
import { CLASS_NAMES } from '../../../core/sim/adapt';
import { HeroSprite } from '../../components';
import { EmoteAnchor } from '../../emote/EmoteBubble';
import { openHeroSheet } from '../../heroes/heroSheetStore';
import '../../heroes/heroes.css';
import { ChatDock, playerColour, usePlayerColours } from '../ChatDock';
import { useHere } from '../Lobby';
import { STORY } from '../../../core/story/genesis';
import { useWriting, WRITING_MIN_MS } from '../../../core/stores/writing';
import { worldTitle } from '../../../core/story/worldName';
import { SceneBackdrop } from './Scene';
import { playSfx } from '../../../core/audio';
import { usePlace } from '../../../core/audio/places';
import { OPENING_LINE, WRITING_LINES } from './writingLines';
import './intro.css';

/** About how long a story takes to write; the rule reaches its hold at the end of it. */
export const STORY_ESTIMATE_MS = 5 * 60_000;
/** Where the rule waits for a story still being written. */
const RULE_HOLD = 0.9;
/** docs/67 SF5: from here past the request the screen says the story is running late. */
export const STORY_LATE_MS = 7 * 60_000;

/** How long each loading line stays up. */
const LINE_MS = 4500;

/** The opening line, then every other line in a fresh order. */
function shuffledLines(): string[] {
  const rest = WRITING_LINES.filter((l) => l !== OPENING_LINE);
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const t = rest[i]!;
    rest[i] = rest[j]!;
    rest[j] = t;
  }
  return [OPENING_LINE, ...rest];
}

/** `written`: the story is already told and the rule only fills the screen's floor, to the end. */
/** Heroes stand at the scene's scale (3×, as the ground is drawn) until the line gets crowded. */
const CROWD = 8;

/** Every player at the table and their heroes, keeper first, standing on the ground: each hero
 *  named at its feet, each player's band on a rule in their colour with their name under it. */
function WritingParty({ worldId }: { worldId: bigint }) {
  const me = useSession((s) => s.identity ?? '');
  const world = useWorld(worldId);
  const members = useWorldMembers(worldId);
  const heroes = useWorldHeroes(worldId).filter((h) => h.status !== 2);
  const colours = usePlayerColours(worldId);
  const keeper = identityHex(world?.owner);
  const sorted = [...members].sort((a, b) => (identityHex(a.owner) === keeper ? -1 : identityHex(b.owner) === keeper ? 1 : a.id < b.id ? -1 : 1));
  if (sorted.length === 0) return null;
  const scale = heroes.length > CROWD ? 2 : 3;
  return (
    <ul className="fk-writing__ground fk-hit" aria-label="The party" data-testid="writing-party" style={{ ['--fk-hero-px' as string]: `${24 * scale}px` }}>
      {sorted.map((m) => {
        const hex = identityHex(m.owner);
        const theirs = heroes.filter((h) => identityHex(h.owner) === hex).sort((a, b) => (a.id < b.id ? -1 : 1));
        return (
          <li key={m.id.toString()} className="fk-writing__band" style={{ ['--fk-seat' as string]: playerColour(colours.get(hex)) }} data-testid="writing-player" data-name={m.displayName}>
            <span className="fk-writing__heroes">
              {theirs.length === 0 && <span className="fk-writing__nohero" aria-hidden="true">?</span>}
              {theirs.map((h) => (
                <span key={h.id.toString()} className="fk-writing__figure">
                  <button type="button" className="fk-writing__hero fk-hero-open" onClick={() => openHeroSheet(h.id)} aria-label={`${h.name}: see their sheet`} data-testid="writing-hero" data-hero-name={h.name}>
                    <HeroSprite heroId={h.id} species={h.species} frame={h.oryxFrame} classId={CLASS_NAMES[h.class] ?? 'fighter'} scale={scale} title={h.name} />
                    <EmoteAnchor heroes={[{ id: h.id, name: h.name, mine: hex === me }]} />
                  </button>
                  <span className="fk-writing__hero-name">{h.name}</span>
                </span>
              ))}
            </span>
            <span className="fk-writing__player">{m.displayName}{hex === me ? ' (you)' : ''}</span>
          </li>
        );
      })}
    </ul>
  );
}

function WritingChat({ worldId }: { worldId: bigint }) {
  const here = useHere(worldId);
  return <div className="fk-writing__chat fk-hit"><ChatDock worldId={worldId} variant="panel" title="World chat" here={here} /></div>;
}

/** docs/67: a story the module gave up on: why, and what the table can do about it. */
export interface WritingFailure { trouble: string; retrying: boolean; onRetry(): void; onLeave(): void }

/** `worldId`: the table waiting on it (the party and the chat); the preview harness has none.
 *  `failed`: the story will not come (docs/67). */
export function WritingView({ worldName, startMs, untilMs, now, written = false, worldId, failed }: { worldName: string; startMs: number; untilMs: number; now: number; written?: boolean; worldId?: bigint; failed?: WritingFailure }) {
  const [lines] = useState(shuffledLines);
  const [k, setK] = useState(0);
  useEffect(() => { if (failed) return; const t = setInterval(() => setK((x) => x + 1), LINE_MS); return () => clearInterval(t); }, [failed !== undefined]);
  const span = Math.max(1, untilMs - startMs);
  const t = Math.max(0, Math.min(1, (now - startMs) / span));
  const p = Math.max(0.03, written ? t : t * RULE_HOLD);
  // docs/44: the storyteller's quiet hall; keys and pages as each line turns over
  usePlace({ key: 'place.study' });
  useEffect(() => { if (k > 0) playSfx('cine.writing', { gain: 0.45 }); }, [k]);
  const late = !written && !failed && now - startMs >= STORY_LATE_MS;
  if (failed) {
    return (
      <main className={`fk-intro fk-writing fk-writing--failed ${worldId !== undefined ? 'fk-writing--table' : ''}`} data-testid="story-failed">
        <SceneBackdrop scene="Night" />
        <div className="fk-writing__body fk-hit">
          <span className="fk-intro__eyebrow">The World could not be written</span>
          <h1 className="fk-writing__world">{worldName}</h1>
          <div className="fk-writing__desk" aria-hidden="true"><span className="fk-writing__candle"><i /></span><span className="fk-writing__quill" /></div>
          <p className="fk-writing__line" role="alert" data-testid="story-trouble">{failed.trouble || 'The storyteller could not write this World.'}</p>
          <p className="fk-writing__hint">Nothing of your company is lost. Ask the storyteller to try again, or come back to it later from your Worlds.</p>
          <div className="fk-writing__actions">
            <Button primary onClick={failed.onRetry} disabled={failed.retrying} data-testid="story-retry">{failed.retrying ? 'Asking…' : 'Ask the storyteller again'}</Button>
            <Button onClick={failed.onLeave} data-testid="story-leave">Back to the title</Button>
          </div>
        </div>
        {worldId !== undefined && <WritingParty worldId={worldId} />}
        {worldId !== undefined && <WritingChat worldId={worldId} />}
      </main>
    );
  }
  return (
    <main className={`fk-intro fk-writing ${worldId !== undefined ? 'fk-writing--table' : ''}`} data-testid="story-writing">
      <SceneBackdrop scene="Night" />
      <div className="fk-writing__body fk-hit">
        <span className="fk-intro__eyebrow">A new World is being written</span>
        <h1 className="fk-writing__world">{worldName}</h1>
        <div className="fk-writing__desk" aria-hidden="true"><span className="fk-writing__candle"><i /></span><span className="fk-writing__quill" /></div>
        <p className="fk-writing__line" key={k} aria-live="polite">{written && p >= 1 ? 'The tale is written.' : lines[k % lines.length]}</p>
        <div className="fk-writing__rule" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(p * 100)}><i style={{ width: `${p * 100}%` }} /></div>
        <p className="fk-writing__hint">Its history, its villain, its first campaign and the battle you open it with are written for this World alone.</p>
      </div>
      {worldId !== undefined && <WritingParty worldId={worldId} />}
      {worldId !== undefined && <WritingChat worldId={worldId} />}
      {!written && (late
        ? <p className="fk-writing__eta fk-writing__eta--late" data-testid="story-late">This one is taking longer than usual. If the storyteller cannot finish it, this screen will say so.</p>
        : <p className="fk-writing__eta">A new World usually takes about 5 minutes to write.</p>)}
    </main>
  );
}

export function WritingScreen() {
  const worldId = useSession((s) => s.worldId);
  const world = useWorld(worldId);
  const row = useWorldStory(worldId);
  const since = useWriting((s) => (worldId !== undefined ? s.since[worldId.toString()] : undefined));
  const told = row?.state === STORY.TOLD && since !== undefined;
  const go = useRoute((s) => s.go);
  const [retrying, setRetrying] = useState(false);
  const clock = () => (told ? deviceNow() : serverNow());
  const [now, setNow] = useState(clock);
  useEffect(() => { const t = setInterval(() => setNow(clock()), 250); return () => clearInterval(t); }, [told]);
  // a told story (written ahead) fills the rule over the floor, on the device clock the floor runs on
  if (told) return <WritingView worldName={worldTitle(world)} startMs={since} untilMs={since + WRITING_MIN_MS} now={now} written worldId={worldId} />;
  if (row?.state === STORY.FAILED && worldId !== undefined) {
    const failed: WritingFailure = {
      trouble: row.trouble,
      retrying,
      onRetry: () => { setRetrying(true); void rpc.retryStory(worldId).finally(() => setRetrying(false)); },
      onLeave: () => go('title'),
    };
    return <WritingView worldName={worldTitle(world)} startMs={now} untilMs={now} now={now} worldId={worldId} failed={failed} />;
  }
  const start = tsToMs(row?.requestedAt) ?? now;
  return <WritingView worldName={worldTitle(world)} startMs={start} untilMs={start + STORY_ESTIMATE_MS} now={now} worldId={worldId} />;
}
